import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import countryService from '@/services/countryService';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Globe, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';

// Infobulles pour la vue géographique
const GEO_TOOLTIPS = {
  geographic: "Répartition de votre portefeuille par pays ou région. Permet de visualiser votre exposition géographique.",
  riskScore: "Score de risque pays (0-100). Plus c'est élevé, plus le pays est considéré comme stable et favorable à l'investissement.",
  exposure: "Pourcentage de votre portefeuille investi dans ce pays, basé sur le siège des entreprises.",
  gdpGrowth: "Croissance économique du pays sur 12 mois. Indicateur de santé économique.",
  inflation: "Taux d'inflation du pays. Une inflation élevée peut éroder les rendements réels.",
};

// Simplified GeoJSON with major countries
const WORLD_GEOJSON = {
  type: "FeatureCollection",
  features: [
    // USA
    {
      type: "Feature",
      properties: { name: "United States", code: "USA" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-125, 50], [-125, 25], [-65, 25], [-65, 50], [-125, 50]
        ]]
      }
    },
    // Netherlands
    {
      type: "Feature",
      properties: { name: "Netherlands", code: "NLD" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [3, 53.5], [3, 50.7], [7.2, 50.7], [7.2, 53.5], [3, 53.5]
        ]]
      }
    },
    // France
    {
      type: "Feature",
      properties: { name: "France", code: "FRA" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-5, 51], [-5, 42], [10, 42], [10, 51], [-5, 51]
        ]]
      }
    },
    // Germany
    {
      type: "Feature",
      properties: { name: "Germany", code: "DEU" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [5.5, 55], [5.5, 47], [15, 47], [15, 55], [5.5, 55]
        ]]
      }
    },
    // Switzerland
    {
      type: "Feature",
      properties: { name: "Switzerland", code: "CHE" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [5.9, 47.8], [5.9, 45.8], [10.5, 45.8], [10.5, 47.8], [5.9, 47.8]
        ]]
      }
    },
    // UK
    {
      type: "Feature",
      properties: { name: "United Kingdom", code: "GBR" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-8, 60], [-8, 49], [2, 49], [2, 60], [-8, 60]
        ]]
      }
    },
    // Taiwan
    {
      type: "Feature",
      properties: { name: "Taiwan", code: "TWN" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [120, 25.5], [120, 21.5], [122, 21.5], [122, 25.5], [120, 25.5]
        ]]
      }
    },
    // South Korea
    {
      type: "Feature",
      properties: { name: "South Korea", code: "KOR" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [125, 39], [125, 33], [130, 33], [130, 39], [125, 39]
        ]]
      }
    },
    // Japan
    {
      type: "Feature",
      properties: { name: "Japan", code: "JPN" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [128, 46], [128, 30], [146, 30], [146, 46], [128, 46]
        ]]
      }
    },
    // China
    {
      type: "Feature",
      properties: { name: "China", code: "CHN" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [73, 53], [73, 18], [135, 18], [135, 53], [73, 53]
        ]]
      }
    },
    // Brazil
    {
      type: "Feature",
      properties: { name: "Brazil", code: "BRA" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-74, 5], [-74, -34], [-34, -34], [-34, 5], [-74, 5]
        ]]
      }
    },
    // India
    {
      type: "Feature",
      properties: { name: "India", code: "IND" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [68, 35], [68, 8], [97, 8], [97, 35], [68, 35]
        ]]
      }
    }
  ]
};

const getColor = (score) => {
  if (score > 80) return '#00ff88';      // Green - low risk
  if (score >= 65) return '#ffa502';    // Yellow - medium risk
  if (score > 0) return '#ff4757';      // Red - high risk
  return '#cccccc';                     // Gray - no data
};

const MapLegend = () => {
  return (
    <Card className="absolute bottom-8 left-8 p-4 z-[1000] bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm">
      <div className="flex items-center gap-2 mb-3">
        <h4 className="text-sm font-semibold">Risk Level</h4>
        <InfoTooltip content={GEO_TOOLTIPS.riskScore} />
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 rounded" style={{ backgroundColor: '#00ff88' }}></div>
          <span className="text-xs">Low (&gt; 80)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 rounded" style={{ backgroundColor: '#ffa502' }}></div>
          <span className="text-xs">Medium (65-80)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 rounded" style={{ backgroundColor: '#ff4757' }}></div>
          <span className="text-xs">High (&lt; 65)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-4 rounded bg-gray-300"></div>
          <span className="text-xs">No Data</span>
        </div>
      </div>
    </Card>
  );
};

const WorldMap = ({ onCountryClick }) => {
  const [countryData, setCountryData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadCountryData();
  }, []);

  const loadCountryData = async () => {
    try {
      setIsLoading(true);
      const response = await countryService.getCountryRiskData();
      
      // Convert array to map for easy lookup
      const dataMap = {};
      response.countries.forEach(country => {
        dataMap[country.id] = country;
      });
      
      setCountryData(dataMap);
    } catch (error) {
      console.error('Error loading country data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load country risk data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onEachFeature = (feature, layer) => {
    const countryCode = feature.properties.code;
    const data = countryData[countryCode];
    
    if (data) {
      // Tooltip content
      const tooltipContent = `
        <div class="p-2">
          <h3 class="font-bold text-base mb-2">${data.flag} ${data.name}</h3>
          <div class="space-y-1 text-sm">
            <p><strong>Risk Score:</strong> ${data.risk_score}</p>
            <p><strong>GDP Growth:</strong> ${data.gdp_growth}%</p>
            <p><strong>Inflation:</strong> ${data.inflation}%</p>
            <p><strong>Stocks:</strong> ${data.stocks.join(', ')}</p>
            <p class="text-xs text-gray-600 mt-2">${data.description}</p>
          </div>
        </div>
      `;
      
      layer.bindTooltip(tooltipContent, {
        sticky: true,
        className: 'custom-tooltip'
      });
      
      // Click handler
      layer.on({
        click: () => {
          setSelectedCountry(data);
          if (onCountryClick) {
            onCountryClick(data);
          }
          toast({
            title: `${data.flag} ${data.name}`,
            description: `Click "View Stocks" to see ${data.stock_count} stocks from this country`,
          });
        },
        mouseover: (e) => {
          const layer = e.target;
          layer.setStyle({
            weight: 3,
            color: '#ffffff',
            dashArray: '',
            fillOpacity: 0.9
          });
        },
        mouseout: (e) => {
          const layer = e.target;
          const score = data.risk_score;
          layer.setStyle({
            weight: 2,
            color: '#ffffff',
            dashArray: '3',
            fillOpacity: 0.7
          });
        }
      });
    }
  };

  const style = (feature) => {
    const countryCode = feature.properties.code;
    const data = countryData[countryCode];
    const score = data ? data.risk_score : 0;
    
    return {
      fillColor: getColor(score),
      weight: 2,
      opacity: 1,
      color: '#ffffff',
      dashArray: '3',
      fillOpacity: 0.7
    };
  };

  const handleViewStocks = () => {
    if (selectedCountry) {
      navigate('/screening', {
        state: { filterCountry: selectedCountry.code }
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6 h-[600px] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected Country Info */}
      {selectedCountry && (
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">
                {selectedCountry.flag} {selectedCountry.name}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    Risk Score <InfoTooltip content={GEO_TOOLTIPS.riskScore} />
                  </p>
                  <p className="text-xl font-bold" style={{ color: getColor(selectedCountry.risk_score) }}>
                    {selectedCountry.risk_score}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    GDP Growth <InfoTooltip content={GEO_TOOLTIPS.gdpGrowth} />
                  </p>
                  <p className="text-xl font-bold">{selectedCountry.gdp_growth}%</p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    Inflation <InfoTooltip content={GEO_TOOLTIPS.inflation} />
                  </p>
                  <p className="text-xl font-bold">{selectedCountry.inflation}%</p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">Stocks</p>
                  <p className="text-xl font-bold">{selectedCountry.stock_count}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {selectedCountry.description}
              </p>
            </div>
            <Button onClick={handleViewStocks} className="ml-4">
              <TrendingUp className="w-4 h-4 mr-2" />
              View Stocks
            </Button>
          </div>
        </Card>
      )}

      {/* Map */}
      <Card className="overflow-hidden relative" style={{ height: '600px' }}>
        <MapContainer
          center={[30, 10]}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <GeoJSON
            data={WORLD_GEOJSON}
            style={style}
            onEachFeature={onEachFeature}
          />
        </MapContainer>
        
        <MapLegend />
      </Card>

      {/* Country List */}
      <Card className="p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Globe className="w-6 h-6 text-blue-500" />
          Country Risk Scores
          <InfoTooltip content={GEO_TOOLTIPS.riskScore} />
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.values(countryData)
            .sort((a, b) => b.risk_score - a.risk_score)
            .map(country => (
              <div
                key={country.id}
                className="p-3 rounded-lg border-2 hover:border-blue-500 transition-colors cursor-pointer"
                style={{ borderColor: selectedCountry?.id === country.id ? '#3b82f6' : 'transparent' }}
                onClick={() => {
                  setSelectedCountry(country);
                  toast({
                    title: `${country.flag} ${country.name}`,
                    description: `Risk Score: ${country.risk_score}`,
                  });
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">
                    {country.flag} {country.name}
                  </span>
                  <span
                    className="text-lg font-bold"
                    style={{ color: getColor(country.risk_score) }}
                  >
                    {country.risk_score}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {country.stock_count} stock{country.stock_count !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
};

export default WorldMap;
